import { useState, useEffect } from "react";

import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import Pagination from "../../../components/Pagination/Pagination";
import {
  ReportActionButtons,
  SECTION_KEYS,
} from "../../screened-manuscripts/components/ReportActionButtons";
import {
  ApplyAction,
  ColorizeStatusText,
} from "../../../components/ApplyAction";
import {
  convertISOToReadableDate,
  getAmbientScreeningSubmissionById,
  showErrorToast,
} from "../../../helpers";
import {
  handleSetTriggerOrMatchStatus,
  handleSetSubmissionStatus,
} from "../../../helpers/statusHelpers";
import { DataTable } from "stm-frontend-library";
import { EmptyInbox, SignalReportModal } from "../../../components";
import { FilterIconHeader } from "../../../components/FilterIconHeader";
import { SwitchForm } from "../../../components/SwitchForm";
import { dropdownOptions } from "../dropdownOptions";
import useDuplicateSubmissions from "../hooks/useDuplicateSubmissions";
import ConfirmationDialog from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { getDataTableColumns } from "../columns";
import { exportManuscriptsToExcel } from "../export";
import {
  FILTER_OPTIONS,
  DEFAULT_FILTER_VALUES,
  filterSelectionToString,
} from "../filterOptions";

const ACTIONS_REQUIRING_CONFIRMATION = ["duplicate confirmed", "suspect paper mill"];

const ManuscriptsTab = ({ type, exportRef, tabKey }) => {
  const [selectedItem, setSelectedItem] = useState({
    index: null,
    visibleSection: SECTION_KEYS.ALL,
  });
  const [fetchedData, setFetchedData] = useState(null);
  const [isFetchingAmbientData, setIsFetchingAmbientData] = useState(false);
  const [filterSelection, setFilterSelection] = useState(DEFAULT_FILTER_VALUES);
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    actionType: null,
    actionContext: null,
  });

  const isInboxTabSelected = type === "wo_status";
  const filterString = filterSelectionToString(filterSelection);

  const {
    data,
    fetchData,
    setData,
    error,
    pageCount,
    currentPage,
    setCurrentPage,
    fetchAllDataForExport,
    isLoading,
  } = useDuplicateSubmissions({ type, filter: filterString });

  const updateStatus = async (index, status, additionalParams = {}) => {
    const newData = [...data];
    const { match_id, persistent_id } = data[index];

    if (isInboxTabSelected) {
      const previousElement = { ...newData[index] };
      newData.splice(index, 1);
      setData(newData);

      try {
        await handleSetTriggerOrMatchStatus(
          match_id,
          persistent_id,
          status,
          additionalParams
        );
        await fetchData();
      } catch {
        showErrorToast();
        newData.splice(index, 0, previousElement);
        setData(newData);
      }
    } else {
      const previousStatus = newData[index].submission_status;
      newData[index].submission_status = status;

      try {
        await handleSetTriggerOrMatchStatus(
          match_id,
          persistent_id,
          status,
          additionalParams
        );
      } catch {
        showErrorToast();
        newData[index].submission_status = previousStatus;
      } finally {
        setData(newData);
      }
    }
  };

  const handleIconClick = (index, buttonKey) => {
    setSelectedItem({ index, visibleSection: buttonKey });
  };

  const handleRowClick = (index) => {
    if (index === null) return;
    setSelectedItem({ index, visibleSection: SECTION_KEYS.DUPSUB });
  };

  const onActionSelect = (index, status, additionalParams = {}) => {
    updateStatus(index, status, additionalParams);
  };

  const onActionUndo = (index) => {
    updateStatus(index, null);
  };

  const handleInterceptedAction = (index, value) => {
    if (ACTIONS_REQUIRING_CONFIRMATION.includes(value)) {
      setConfirmationDialog({
        isOpen: true,
        actionType: value,
        actionContext: { index, value },
      });
      return;
    }
    onActionSelect(index, value);
  };

  const handleConfirmationApply = (options) => {
    const { actionContext } = confirmationDialog;
    if (actionContext) {
      const item = data[actionContext.index];
      const fullParams = {
        ...(options.addToSignalcheck && {
          signalCheck: {
            user: {
              comment: options.comment || "",
            },
            manuscript: {
              title: item.title || "",
              manuscript_id: item.manuscript_id || "",
              publisher_id: item.publisher_id || "",
              user_id: item.user_id,
            },
          },
        }),
      };
      onActionSelect(actionContext.index, actionContext.value, fullParams);
    }
    setConfirmationDialog({ isOpen: false, actionType: null, actionContext: null });
  };

  const handleConfirmationClose = () => {
    setConfirmationDialog({ isOpen: false, actionType: null, actionContext: null });
  };

  const handleFilterSubmit = (values) => setFilterSelection(values);
  const handleFilterReset = () => setFilterSelection(DEFAULT_FILTER_VALUES);

  const filterComponent = (
    <FilterIconHeader label="Status" dropdownTitle="Signal Type Selection">
      <SwitchForm
        handleSubmit={handleFilterSubmit}
        handleReset={handleFilterReset}
        initialValues={filterSelection}
        options={FILTER_OPTIONS}
      />
    </FilterIconHeader>
  );

  const tableColumns = getDataTableColumns(filterComponent);

  const dataWithActions = data.map((element, index) => ({
    ...element,
    raw_match_status: element.match_status,
    timestamp: convertISOToReadableDate(element.timestamp, "DD MMM YYYY"),
    match_status: <ColorizeStatusText statusText={element.match_status} />,
    action: (
      <div className="d-flex">
        <ApplyAction
          index={index}
          status={element.submission_status}
          selectAction={handleInterceptedAction}
          undoAction={onActionUndo}
          options={dropdownOptions}
        />
      </div>
    ),
    signals: (
      <ReportActionButtons
        signals={element.signals}
        onIconClick={(buttonKey) => handleIconClick(index, buttonKey)}
      />
    ),
    clickEvent: () => handleRowClick(index),
  }));

  const fetchAmbientScreeningDataIfNeeded = async (index) => {
    const { persistent_id, signals } = data[index];
    if (persistent_id && signals?.some((s) => s !== "duplicateSubmission")) {
      try {
        setIsFetchingAmbientData(true);
        const { data: fetchedData } = await getAmbientScreeningSubmissionById(
          persistent_id
        );
        setFetchedData(fetchedData);
      } finally {
        setIsFetchingAmbientData(false);
      }
    }
  };

  useEffect(() => {
    if (selectedItem.index !== null) {
      fetchAmbientScreeningDataIfNeeded(selectedItem.index);
    } else {
      setFetchedData(null);
    }
  }, [selectedItem.index]);

  const handleExport = async () => {
    try {
      const allData = await fetchAllDataForExport();
      if (allData?.length > 0) {
        const fileNames = {
          wo_status: "InboxManuscripts",
          all: "AllManuscripts",
          metadata: "MetadataManuscripts",
          fts_only: "FTSManuscripts",
        };
        exportManuscriptsToExcel(allData, fileNames[type] || "DuplicateSubmissions");
      }
    } catch {
      showErrorToast("Failed to export data");
    }
  };

  useEffect(() => {
    if (exportRef && tabKey) {
      exportRef.current[tabKey] = handleExport;
    }
  }, [exportRef, tabKey]);

  const resetModalState = () => {
    setFetchedData(null);
    setSelectedItem({ index: null, visibleSection: SECTION_KEYS.ALL });
  };

  const hasNonDupSubSignals =
    selectedItem.index !== null &&
    !isFetchingAmbientData &&
    data[selectedItem.index]?.signals?.some((x) => x !== "duplicateSubmissions");

  const shouldRenderModal =
    selectedItem.index !== null &&
    (fetchedData || data[selectedItem.index]) &&
    (hasNonDupSubSignals ? fetchedData : true);

  return (
    <>
      <ConfirmationDialog
        actionType={confirmationDialog.actionType}
        isOpen={confirmationDialog.isOpen}
        onClose={handleConfirmationClose}
        onApply={handleConfirmationApply}
      />

      {shouldRenderModal && (
        <SignalReportModal
          dupSubData={data[selectedItem.index]}
          contentData={fetchedData}
          visibleSection={selectedItem.visibleSection}
          onClose={resetModalState}
          setTriggerOrMatchStatus={handleSetTriggerOrMatchStatus}
          setSubmissionStatus={handleSetSubmissionStatus}
          fetchData={fetchData}
        />
      )}

      {error && <ErrorMessage />}
      {!error && (
        <>
          <DataTable
            columns={tableColumns}
            rows={dataWithActions}
            customNoDataComponent={isInboxTabSelected ? EmptyInbox : null}
            isLoading={isLoading}
            loadingMode="overlay"
          />
          <Pagination
            handlePageClick={(e) => setCurrentPage(e.selected)}
            pageCount={pageCount}
            currentPage={currentPage}
            disabled={isLoading}
          />
        </>
      )}
    </>
  );
};

export default ManuscriptsTab;
